import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Clock, Plane, AlertTriangle, ArrowRight, Shield, Brain, Star, Menu, Share2, CheckCircle, Download, Apple } from "lucide-react";
import appIcon from "@/assets/app-icon-1024.png";
import { APP_STORE_URL, trackCtaClick } from "@/data/appLinks";
import { useDemoWaitTimes } from "@/hooks/useDemoWaitTimes";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { airports } from "@/data/airports";
import { Helmet } from "react-helmet-async";
import { 
  SchemaMarkup, 
  organizationSchema, 
  webApplicationSchema,
  serviceSchema,
  generateFAQSchema, 
  generateHowToSchema,
  generateAirportListSchema,
  generateBreadcrumbSchema
} from "@/components/SchemaMarkup";
import { SignupBanner } from "@/components/SignupBanner";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";
import { EmailCapture } from "@/components/EmailCapture";
import { CURRENT_YEAR } from "@/lib/currentYear";

const Landing = () => {
  const { waitTimes, loading } = useDemoWaitTimes();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const demoAirports = [
    { code: "SFO", terminal: "All checkpoints" },
    { code: "MSP", terminal: "All checkpoints" },
    { code: "STL", terminal: "All checkpoints" },
    { code: "LAX", terminal: "Terminal 3" },
    { code: "ORD", terminal: "Terminal 1" }
  ];

  const faqData = [
    {
      question: "Where does your TSA wait time data come from?",
      answer: "Where an airport publishes its own checkpoint data, we show those live numbers and refresh them every 10 minutes, including TSA PreCheck lanes. Everywhere else we show the typical wait for that airport at this day and time, and we always label which one you are looking at."
    },
    {
      question: "Which airports do you cover?",
      answer: "We currently cover 100+ major US airports including all major hubs (DFW, LAX, JFK, ORD, ATL) and most regional airports. We're constantly adding new airports based on traveler requests."
    },
    {
      question: "Is Airport Buddy really free?",
      answer: "Yes! Airport Buddy is 100% free to use with no hidden fees, no credit card required, and no premium tiers. We believe everyone deserves stress-free travel."
    },
    {
      question: "How accurate are the wait times?",
      answer: "Live numbers come straight from the airport's own checkpoint systems, so they are as current as the airport's own screens. Typical waits are based on historical patterns for that day and hour, and are clearly marked as estimates. Conditions can change quickly at peak times."
    },
    {
      question: "Do you offer notifications or alerts?",
      answer: "Yes! Create a free account to save your favorite airports and receive notifications when wait times change significantly, helping you time your arrival perfectly."
    },
    {
      question: "Is my data private?",
      answer: "Absolutely. We don't track your searches, we don't sell your data, and we don't show ads. Your privacy is our priority. We only collect minimal information needed to provide the service."
    }
  ];

  const howToSteps = [
    { name: "Select Your Airport", text: "Choose your departure airport from our list of 100+ US airports" },
    { name: "View Live Wait Times", text: "See current TSA security checkpoint wait times updated in real-time" },
    { name: "Check Flight Status", text: "View your flight's departure time, gate, and any delays" },
    { name: "Plan Your Departure", text: "Leave at the optimal time to avoid stress and long lines" }
  ];

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied! Share it with your travel companion.");
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Live TSA Wait Times Right Now – 100+ US Airports</title>
        <meta name="description" content="Check TSA wait times RIGHT NOW at 100+ airports. Live security lines updated every 5 min. ⏱️ See your airport's wait time before you leave home." />
        <meta name="keywords" content="TSA wait time right now, airport security wait time today, live TSA wait times, airport security lines, TSA checkpoint times, current airport security, airport wait times today, real-time wait times" />
        <link rel="canonical" href="https://airportbuddy.app/" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Freshness signals */}
        <meta property="article:modified_time" content="2026-01-30" />
        <meta property="og:updated_time" content="2026-01-30" />
        
        {/* Open Graph */}
        <meta property="og:title" content="TSA Wait Times Right Now – Check Before You Leave" />
        <meta property="og:description" content="See live airport security wait times for 100+ US airports. Updated every 5 minutes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://airportbuddy.app/" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TSA Wait Times Right Now – Live Lines at 100+ Airports" />
        <meta name="twitter:description" content="Check real-time airport security wait times before you leave home. Updated every 5 min." />
      </Helmet>

      {/* Schema Markup for SEO */}
      <SchemaMarkup schema={organizationSchema} />
      <SchemaMarkup schema={webApplicationSchema} />
      <SchemaMarkup schema={serviceSchema} />
      <SchemaMarkup schema={generateFAQSchema(faqData)} />
      <SchemaMarkup schema={generateHowToSchema(
        "How to Check TSA Wait Times Before Your Flight",
        "Use AirportBuddy to check real-time TSA security wait times and plan your airport arrival time perfectly.",
        howToSteps,
        "PT2M"
      )} />
      <SchemaMarkup schema={generateAirportListSchema(airports)} />
      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" }
      ])} />

      {/* Urgent Top Banner */}
      <div className="bg-destructive text-destructive-foreground py-2 px-4 text-center text-sm font-medium">
        <Link to="/auth" className="hover:underline inline-flex items-center gap-2">
          🚨 Running late? Check TSA wait times before you leave →
        </Link>
      </div>

      {/* Navbar */}
      <nav className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <img src={appIcon} alt="Airport Buddy" className="h-9 w-9 rounded-xl" />
              <span className="text-lg font-bold text-foreground tracking-tight">Airport Buddy</span>
            </Link>
            
            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                How It Works
              </a>
              <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Features
              </a>
              <a href="#faq" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                FAQ
              </a>
              <Link to="/install" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
                <Download className="h-4 w-4" />
                Install App
              </Link>
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <ThemeToggle />
              <Button asChild variant="ghost">
                <Link to="/auth">Sign In</Link>
              </Button>
              <Button asChild>
                <Link to="/auth">Check My Airport Now</Link>
              </Button>
            </div>

            {/* Mobile Menu */}
            <div className="flex items-center gap-1 md:hidden">
              <ThemeToggle />
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open mobile menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px]">
                  <nav className="flex flex-col gap-6 mt-8">
                    <a 
                      href="#how-it-works" 
                      className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      How It Works
                    </a>
                    <a 
                      href="#features" 
                      className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Features
                    </a>
                    <a 
                      href="#faq" 
                      className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      FAQ
                    </a>
                    <Link 
                      to="/install" 
                      className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Download className="h-5 w-5" />
                      Install App
                    </Link>
                    <div className="flex flex-col gap-3 pt-4 border-t border-border">
                      <Button asChild variant="ghost" className="w-full">
                        <Link to="/auth">Sign In</Link>
                      </Button>
                      <Button asChild className="w-full">
                        <Link to="/auth">Check My Airport Now</Link>
                      </Button>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      <main>
      {/* Hero Section - Above the fold */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-destructive/5 via-background to-primary/5" />
        <div className="relative container mx-auto px-4 py-16 sm:py-24">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground">
              Real-Time Airport Security Wait Times
            </h1>
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto">
              See live TSA checkpoint lines for 100+ airports. Know exactly when to leave home — never miss a flight again.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button asChild size="lg" className="text-lg h-14 px-8">
                <Link to="/auth">
                  Check My Airport Now <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="text-lg h-14 px-8"
                onClick={() => trackCtaClick("landing_hero")}
              >
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <Apple className="mr-2 h-5 w-5" aria-hidden="true" />
                  Download for iPhone
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proof Bar - 3 items */}
      <section className="py-8 bg-muted/50 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-3 text-center sm:text-left">
              <div className="rounded-full w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Live TSA Wait Times</h2>
                <p className="text-sm text-muted-foreground">Real checkpoint data, not estimates.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 text-center sm:text-left">
              <div className="rounded-full w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <Plane className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Real-Time Flight Status</h2>
                <p className="text-sm text-muted-foreground">Delays, gate changes, boarding updates.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 text-center sm:text-left">
              <div className="rounded-full w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <Brain className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Smart Leave-Time Guidance</h2>
                <p className="text-sm text-muted-foreground">Know when to leave based on current conditions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-8">
            Live Wait Times Right Now
          </h2>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
              {demoAirports.map((airport) => (
                <Card key={airport.code} className="p-4 border-border bg-card animate-pulse">
                  <div className="flex items-start justify-between mb-2">
                    <div className="space-y-2">
                      <div className="h-5 w-12 rounded bg-muted skeleton-shimmer" />
                      <div className="h-3 w-20 rounded bg-muted skeleton-shimmer" />
                    </div>
                    <div className="h-5 w-5 rounded bg-muted skeleton-shimmer" />
                  </div>
                  <div className="h-8 w-16 rounded bg-muted skeleton-shimmer mt-2" />
                  <div className="h-3 w-24 rounded bg-muted skeleton-shimmer mt-3" />
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
              {demoAirports.map((airport) => {
                const data = waitTimes[airport.code];
                const waitTime = data?.waitTime || 0;
                const status = data?.status || 'Unknown';
                
                return (
                  <Card 
                    key={airport.code} 
                    className="p-4 border-border bg-card hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-bold text-lg text-card-foreground">{airport.code}</p>
                        <p className="text-sm text-muted-foreground">{airport.terminal}</p>
                      </div>
                      <Clock 
                        className={`h-5 w-5 ${
                          status === "low" ? "text-success" : 
                          status === "medium" ? "text-warning" : "text-destructive"
                        }`} 
                        aria-hidden="true"
                      />
                    </div>
                    <p 
                      className={`text-2xl font-bold ${
                        status === "low" ? "text-success" : 
                        status === "medium" ? "text-warning" : "text-destructive"
                      }`}
                      aria-label={`${waitTime} minute wait time`}
                    >
                      {data?.estimated ? `~${waitTime}` : waitTime} min
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {data?.estimated ? "typical for right now" : "live from the airport"}
                    </p>
                    <Link 
                      to={`/airports/${airport.code.toLowerCase()}`} 
                      className="text-sm text-primary hover:underline mt-2 block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded transition-colors"
                    >
                      View {airport.code} Details →
                    </Link>
                  </Card>
                );
              })}
            </div>
          )}
          
          {/* Social Proof */}
          <div className="text-center mt-10 space-y-4">
            <p className="text-muted-foreground font-medium">
              ✈️ Tracking wait times at <span className="text-foreground font-semibold">100+ US airports</span> — trusted by thousands of travelers
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-xs text-muted-foreground">
              {['ATL', 'LAX', 'ORD', 'DFW', 'DEN', 'JFK', 'SFO', 'SEA', 'MIA', 'EWR'].map(code => (
                <Link 
                  key={code}
                  to={`/airports/${code.toLowerCase()}`}
                  className="px-2.5 py-1 bg-muted rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  {code}
                </Link>
              ))}
            </div>
          </div>

          {/* Share trigger */}
          <div className="text-center mt-6">
            <p className="text-muted-foreground mb-3">Traveling with someone?</p>
            <Button variant="outline" onClick={handleShare} className="gap-2">
              <Share2 className="h-4 w-4" />
              Send This to Someone Flying With Me
            </Button>
          </div>
        </div>
      </section>

      {/* Email Capture */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <EmailCapture source="homepage" />
        </div>
      </section>

      {/* How It Works Section - Skimmable */}
      <section id="how-it-works" className="py-16 bg-muted/30 scroll-mt-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-foreground mb-12">
            How It Works
          </h2>
          
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-primary-foreground">1</span>
              </div>
              <p className="text-lg text-foreground">Select your airport</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-primary-foreground">2</span>
              </div>
              <p className="text-lg text-foreground">See current TSA wait times instantly</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-primary-foreground">3</span>
              </div>
              <p className="text-lg text-foreground">Check your flight status</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-primary-foreground">4</span>
              </div>
              <p className="text-lg text-foreground">Leave at the right time — avoid panic</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Button asChild size="lg">
              <Link to="/auth">Check My Airport Now</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Panic Moment Section */}
      <section className="py-16 bg-destructive/5 border-y border-destructive/20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Running Late? This Is For You.
            </h2>
            
            <div className="space-y-4 text-left max-w-md mx-auto">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
                <p className="text-lg text-muted-foreground">TSA lines suddenly backed up?</p>
              </div>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
                <p className="text-lg text-muted-foreground">Flight delayed or gate changed?</p>
              </div>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
                <p className="text-lg text-muted-foreground">Not sure if you should leave yet?</p>
              </div>
            </div>

            <Button asChild size="lg" className="mt-4">
              <Link to="/auth">
                Check My Airport Now <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Browse Airports Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Browse Airports
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Live TSA wait times for 100+ major US airports
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-w-5xl mx-auto">
            {airports.slice(0, 20).map((airport) => (
              <Link 
                key={airport.code}
                to={`/airports/${airport.code.toLowerCase()}`}
              >
                <Card className="p-3 border-border bg-card hover:shadow-lg hover:border-primary/50 transition-all cursor-pointer h-full">
                  <div className="text-center">
                    <p className="font-bold text-lg text-card-foreground">{airport.code}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">{airport.city}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Button asChild variant="outline">
              <Link to="/auth">See TSA Wait Times</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Trust & Credibility Block */}
      <section id="features" className="py-16 bg-muted/30 scroll-mt-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-foreground mb-12">
            Built for Real Travelers
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
              <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
              <span className="text-foreground">Live data from major US airports</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
              <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
              <span className="text-foreground">No signup required</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
              <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
              <span className="text-foreground">Fast, lightweight, mobile-friendly</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
              <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
              <span className="text-foreground">Designed for last-minute checks</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
              <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
              <span className="text-foreground">No clutter, no ads, no fluff</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
              <Shield className="h-5 w-5 text-success flex-shrink-0" />
              <span className="text-foreground">Privacy-first — we don't track you</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What Travelers Are Saying
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="p-6 border-border bg-card">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>
              <p className="text-card-foreground mb-4">
                "Saved me from missing my connecting flight! Knew exactly when to leave for DFW security."
              </p>
              <p className="text-sm text-muted-foreground font-semibold">— Sarah M., Business Traveler</p>
            </Card>

            <Card className="p-6 border-border bg-card">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>
              <p className="text-card-foreground mb-4">
                "Way better than the official TSA app. More airports and updates faster."
              </p>
              <p className="text-sm text-muted-foreground font-semibold">— Michael R., Frequent Flyer</p>
            </Card>

            <Card className="p-6 border-border bg-card">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>
              <p className="text-card-foreground mb-4">
                "Finally, no more guessing! I check this before every trip now."
              </p>
              <p className="text-sm text-muted-foreground font-semibold">— Jennifer K., Family Traveler</p>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-muted/30 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-foreground mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6 border-border bg-card">
                  <h3 className="text-lg font-semibold text-card-foreground mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Guides Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-foreground mb-4">
              Popular Travel Guides
            </h2>
            <p className="text-center text-muted-foreground mb-10">
              Expert tips to save time at airport security
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* High-priority internal links */}
              <Link to="/tips/minimize-wait-times" className="block group">
                <Card className="p-5 h-full hover:border-primary transition-colors border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">Featured</span>
                  </div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    Top 5 Tips to Minimize Wait Times
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Save 30+ minutes at security with these proven strategies.
                  </p>
                </Card>
              </Link>
              
              <Link to="/real-time-wait-times" className="block group">
                <Card className="p-5 h-full hover:border-primary transition-colors border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">Live</span>
                  </div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    Real-Time Airport Security Wait Times
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Check live TSA wait times updated every 5 minutes.
                  </p>
                </Card>
              </Link>
              
              <Link to="/blog/tips-for-avoiding-long-lines-at-airport-security" className="block group">
                <Card className="p-5 h-full hover:border-primary transition-colors">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    Avoid Long Security Lines
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    15 expert tips to breeze through TSA checkpoints.
                  </p>
                </Card>
              </Link>
              
              <Link to="/blog/tsa-precheck-vs-clear-which-is-better" className="block group">
                <Card className="p-5 h-full hover:border-primary transition-colors">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    TSA PreCheck vs CLEAR
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Compare wait times, costs, and benefits. Which is worth it?
                  </p>
                </Card>
              </Link>
              
              <Link to="/blog/how-early-should-i-arrive-at-airport-for-6am-flight" className="block group">
                <Card className="p-5 h-full hover:border-primary transition-colors">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    Early Morning Flight Guide
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    How early to arrive for 5 AM or 6 AM flights at any airport.
                  </p>
                </Card>
              </Link>
              
              <Link to="/blog" className="block group">
                <Card className="p-5 h-full hover:border-primary transition-colors bg-muted/50">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    All Travel Guides →
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Browse all our airport tips, security guides, and travel advice.
                  </p>
                </Card>
              </Link>
            </div>
            
            {/* Airport Quick Links */}
            <div className="mt-10 pt-8 border-t border-border">
              <h3 className="text-xl font-semibold text-foreground mb-4 text-center">Popular Airport Guides</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  { code: "BOS", name: "Boston Logan" },
                  { code: "BWI", name: "Baltimore-Washington" },
                  { code: "ATL", name: "Atlanta" },
                  { code: "LAX", name: "Los Angeles" },
                  { code: "ORD", name: "Chicago O'Hare" },
                  { code: "JFK", name: "New York JFK" }
                ].map((airport) => (
                  <Link 
                    key={airport.code}
                    to={`/airports/${airport.code.toLowerCase()}/guide`}
                    className="px-4 py-2 bg-muted hover:bg-primary/10 hover:text-primary rounded-full text-sm transition-colors"
                  >
                    {airport.code} Guide
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Don't Miss Your Flight
            </h2>
            <p className="text-lg text-muted-foreground">
              Know when to leave. Avoid long TSA lines. Travel stress-free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg h-14 px-8">
                <Link to="/auth">
                  Check My Airport Now <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Free Forever • No Credit Card • 100+ Airports
            </p>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-foreground mb-4">AirportBuddy</h3>
              <p className="text-sm text-muted-foreground">
                Live TSA wait times and flight status so you never miss a flight.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackCtaClick("landing_footer")}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    iPhone App
                  </a>
                </li>
                <li><a href="#features" className="text-muted-foreground hover:text-foreground">Features</a></li>
                <li><a href="#how-it-works" className="text-muted-foreground hover:text-foreground">How It Works</a></li>
                <li><a href="#faq" className="text-muted-foreground hover:text-foreground">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-4">Top Airports</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/airports/atl" className="text-muted-foreground hover:text-foreground">Atlanta (ATL)</Link></li>
                <li><Link to="/airports/lax" className="text-muted-foreground hover:text-foreground">Los Angeles (LAX)</Link></li>
                <li><Link to="/airports/ord" className="text-muted-foreground hover:text-foreground">Chicago (ORD)</Link></li>
                <li><Link to="/airports/dfw" className="text-muted-foreground hover:text-foreground">Dallas (DFW)</Link></li>
                <li><Link to="/airports/jfk" className="text-muted-foreground hover:text-foreground">New York (JFK)</Link></li>
                <li><Link to="/airports/den" className="text-muted-foreground hover:text-foreground">Denver (DEN)</Link></li>
                <li><Link to="/airports/sfo" className="text-muted-foreground hover:text-foreground">San Francisco (SFO)</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-4">Travel Guides</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog/how-long-is-tsa-wait-time-right-now" className="text-muted-foreground hover:text-foreground">TSA Wait Time Now</Link></li>
                <li><Link to="/blog/tsa-precheck-vs-clear-which-is-better" className="text-muted-foreground hover:text-foreground">PreCheck vs CLEAR</Link></li>
                <li><Link to="/blog/how-early-should-i-arrive-at-airport-for-6am-flight" className="text-muted-foreground hover:text-foreground">Early Morning Flights</Link></li>
                <li><Link to="/blog/fastest-tsa-checkpoints-major-airports" className="text-muted-foreground hover:text-foreground">Fastest Checkpoints</Link></li>
                <li><Link to="/blog/busiest-airport-days-2025" className="text-muted-foreground hover:text-foreground">Busiest Travel Days</Link></li>
                <li><Link to="/guides" className="text-muted-foreground hover:text-foreground">Airport Guides</Link></li>
                <li><Link to="/blog" className="text-muted-foreground hover:text-foreground">All Articles →</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-muted-foreground hover:text-foreground">About</Link></li>
                <li><Link to="/blog" className="text-muted-foreground hover:text-foreground">Blog</Link></li>
                <li><Link to="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link></li>
                <li><Link to="/privacy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-muted-foreground hover:text-foreground">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="text-center text-sm text-muted-foreground pt-8 border-t border-border">
            <p>&copy; {CURRENT_YEAR} AirportBuddy. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Signup Banner */}
      <SignupBanner />
      <ExitIntentPopup />
    </div>
  );
};

export default Landing;
